import React, { useState } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';

interface AdminMFAControlsProps {
  userId: string;
  mfaRequired: boolean;
  mfaSetupComplete: boolean;
  onMFAChange: () => void;
}

const AdminMFAControls: React.FC<AdminMFAControlsProps> = ({
  userId,
  mfaRequired,
  mfaSetupComplete,
  onMFAChange
}) => {
  const [loading, setLoading] = useState(false);

  const handleRequireMFA = async () => {
    if (!window.confirm(`Require MFA for user ${userId}?\n\nThe user will be prompted to set up MFA on their next login.`)) {
      return;
    }

    try {
      setLoading(true);
      await apiCall(`/admin/mfa/require/${userId}`, {
        method: 'POST'
      });

      toast.success('MFA required for user. They will set up MFA on next login.');
      onMFAChange();
    } catch (error: any) {
      console.error('Error requiring MFA:', error);
      toast.error(error.message || 'Failed to require MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleUnrequireMFA = async () => {
    if (!window.confirm(`Remove MFA requirement for user ${userId}?\n\nThe user will no longer need to enter a code when logging in.`)) {
      return;
    }

    try {
      setLoading(true);
      await apiCall(`/admin/mfa/unrequire/${userId}`, {
        method: 'POST'
      });

      toast.success('MFA no longer required for user');
      onMFAChange();
    } catch (error: any) {
      console.error('Error unrequiring MFA:', error);
      toast.error(error.message || 'Failed to unrequire MFA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {!mfaRequired ? (
        <button
          onClick={handleRequireMFA}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          title="Require MFA for this user"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Require MFA
        </button>
      ) : (
        <button
          onClick={handleUnrequireMFA}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          title="Remove MFA requirement for this user"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
          </svg>
          Don't Require MFA
        </button>
      )}
      {mfaRequired && (
        <span className={`text-xs ${mfaSetupComplete ? 'text-green-600' : 'text-yellow-600'}`}>
          {mfaSetupComplete ? '✓ Setup Complete' : '⏳ Setup Pending'}
        </span>
      )}
    </div>
  );
};

export default AdminMFAControls;
